import { useTemplate } from "PFApp/hooks";
import { SidePanelType, useSidePanel, useSidePanelActions } from "PFApp/stores/side_panels_store";
import { Button } from "PFComponents/button";
import { Stack } from "PFComponents/containers";
import { Divider } from "PFComponents/divider";
import { LoadingDots } from "PFComponents/loading_dots";
import SidePanel from "PFComponents/side_panel/side_panel";
import { Typography } from "PFComponents/typography";
import { safePrune } from "PFCore/helpers/safe_prune";
import { useActivity } from "PFCore/hooks/queries/activity";
import { useActivityShortlists } from "PFCore/hooks/queries/shortlists/use_activity_shortlists";
import { useShortlistCreate } from "PFCore/hooks/queries/shortlists/use_shortlist_create";
import { useShortlistDelete } from "PFCore/hooks/queries/shortlists/use_shortlist_delete";
import { useErrorsGrowl } from "PFCore/hooks/use_errors_growl";
import { ActivityPreview as ActivityPreviewType } from "PFTypes";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router";

import { ActivityAudience } from "../activity_commons/activity_audience";
import { ActivityDetailsMoreInfo } from "../activity_commons/activity_details_more_info";
import { ActivityDetailsParticipants } from "../activity_commons/activity_details_participants";
import { ActivityDetailsQuestions } from "../activity_commons/activity_details_questions";
import { ActivityDetailsVacancies } from "../activity_commons/activity_details_vacancies";
import { ActivityResourcingDates } from "../activity_commons/activity_resourcing_dates";
import css from "./activity_preview.module.scss";

export const ActivityPreview = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const location = useLocation();
  const growlErrors = useErrorsGrowl();

  const { close } = useSidePanelActions<ActivityPreviewType>(SidePanelType.ActivityPreview);
  const {
    data: { id, profile },
    isOpen
  } = useSidePanel<ActivityPreviewType>(SidePanelType.ActivityPreview);

  const handleError = useCallback((response) => growlErrors(response), [growlErrors]);

  const { data: activity, isPending: isLoadingActivity } = useActivity(id, {
    enabled: Boolean(isOpen)
  });

  const {
    data: shortlists,
    isFetching: isLoadingShortlists,
    isError: isShortlistsError,
    error: shortlistsError,
    errorUpdatedAt: shortlistsErrorUpdatedAt
  } = useActivityShortlists(
    id,
    {},
    {
      enabled: Boolean(isOpen && profile && activity?.coowned)
    }
  );

  useEffect(() => {
    if (isShortlistsError) {
      handleError(shortlistsError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shortlistsErrorUpdatedAt]);

  const template = useTemplate(activity?.template_id);
  const isRole = template?.key === "role";

  const shortlistItem = (shortlists?.entries || []).find((item) => item.profile.id === profile?.id);

  const { mutate: createShortlist, isPending: isCreatingShortlist } = useShortlistCreate({
    onError: handleError
  });

  const { mutate: deleteShortlist, isPending: isDeletingShortlist } = useShortlistDelete({
    options: {
      onError: handleError
    },
    activityId: id
  });

  useEffect(() => {
    if (!location.pathname?.includes("matching_roles")) {
      // to close panel while navigating
      close();
    }
  }, [location.pathname]);

  const handleAvatarClick = ({ id }) => navigate(`/profiles/${id}`);
  const handleShortlistProfile = () => createShortlist({ activityId: id, profileId: profile?.id });
  const handleRemoveShortlist = () => {
    if (!shortlistItem) {
      return;
    }
    deleteShortlist(shortlistItem.id);
  };

  const isAssigned = shortlistItem?.state === "assigned";

  const prunedName = safePrune(activity?.name, 32);
  const shortlistLoading = isLoadingShortlists || isCreatingShortlist || isDeletingShortlist;

  return (
    <SidePanel
      show={isOpen}
      onClose={close}
      title={
        <Link to={{ pathname: `/activities/${id}` }} state={{ utmSource: location?.state?.utmSource }}>
          <Typography variant="h4">{prunedName}</Typography>
        </Link>
      }
      footerRenderer={() => (
        <div className={css.buttons}>
          <Button text={t("activityPreview.done")} kind="secondary" onClick={close} />
          {activity?.coowned && profile && (
            <Button
              kind={shortlistItem ? "primary" : "secondary"}
              qaId="shortlist-button"
              disabled={isAssigned || shortlistLoading}
              onClick={shortlistItem ? handleRemoveShortlist : handleShortlistProfile}
              icon={shortlistItem && !shortlistLoading ? "check" : undefined}
              text={
                !shortlistLoading && shortlistItem
                  ? t("activityPreview.shortlisted")
                  : t("activityPreview.shortlist")
              }
            />
          )}
        </div>
      )}
    >
      {isLoadingActivity ? (
        <div className={css.loading}>
          <LoadingDots pageViewEnabled circlesEnabled circleSize="sm" />
        </div>
      ) : (
        <Stack gap="spacingLg">
          {activity && (
            <>
              {/* @ts-ignore */}
              <ActivityResourcingDates activity={activity} />
              <Divider />
            </>
          )}
          {activity && (
            <>
              <ActivityAudience activity={activity} />
              <Divider />
            </>
          )}
          {activity?.coowned && isRole && (
            <>
              <ActivityDetailsVacancies activity={activity || {}} />
              <Divider />
            </>
          )}
          <ActivityDetailsMoreInfo
            activity={activity}
            metadataToOmit={["interest_questions", "interest_statement_enabled"]}
          />
          {activity?.metadata?.interest_questions && (
            <>
              <Divider />
              <ActivityDetailsQuestions questions={activity.metadata.interest_questions} showCard={false} />
            </>
          )}
          <Divider />
          <ActivityDetailsParticipants activity={activity} handleAvatarClick={handleAvatarClick} />
        </Stack>
      )}
    </SidePanel>
  );
};
